import { AlertCircle, ShieldAlert } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import { formatCountdown } from '../viewmodels/useLoginBlock';
import type { LoginNotice } from '../viewmodels/useLoginViewModel';
import styles from './LoginNoticeCard.module.scss';

export interface LoginNoticeCardProps {
  notice: LoginNotice;
  /** Segundos restantes, quando o aviso é de bloqueio. */
  remainingSeconds?: number;
}

/**
 * Cartão de aviso da tela de login.
 *
 * Diz **o que aconteceu e o que fazer**, sem culpar o usuário e sem código cru.
 * O aviso vive numa região `aria-live`: quem usa leitor de tela não perde a
 * resposta por ela aparecer longe do campo em foco.
 */
export function LoginNoticeCard({ notice, remainingSeconds = 0 }: LoginNoticeCardProps) {
  const bloqueado = notice.variant === 'blocked';

  return (
    <div className={cn(styles.card, styles[notice.variant])} role="alert">
      <div className={styles.header}>
        <span className={styles.icon}>
          <Icon as={bloqueado ? ShieldAlert : AlertCircle} size={20} />
        </span>
        <p className={styles.title}>{notice.title}</p>

        {bloqueado && remainingSeconds > 0 ? (
          <span className={styles.badge}>
            <span className={styles.countdown}>{formatCountdown(remainingSeconds)}</span>
          </span>
        ) : notice.attemptsRemaining !== undefined ? (
          <span className={styles.badge}>
            {notice.attemptsRemaining === 1
              ? 'última tentativa'
              : `${notice.attemptsRemaining} tentativas`}
          </span>
        ) : null}
      </div>

      <p className={styles.body}>{notice.body}</p>

      {bloqueado && remainingSeconds > 0 ? (
        <p className={styles.body}>
          Você poderá tentar de novo em <strong>{formatCountdown(remainingSeconds)}</strong>.
        </p>
      ) : null}
    </div>
  );
}
